/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import _clone from 'lodash/clone';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import _keys from 'lodash/keys';
import _some from 'lodash/some';

import QUERY_TRANSFERS from '../graphql/transfers';
import apolloClient from '../services/apollo-client';
import { parseResponse } from '../helpers/dataHelpers';

const transfersProvider = {
  getList: async (resource, params) => {
    const userId = _get(params, 'filter.userId');
    const substanceUserId = _get(params, 'filter.substanceUserId');
    if (userId === undefined) return { data: [], total: 0 };

    const response = await fetchTransfers(resource, {
      userId,
      substanceUserId,
    });
    return { data: [response.data], total: 1 };
  },

  getOne: async (resource, params) => {
    const id = _get(params, 'id');
    if (id) {
      const idObj = JSON.parse(id);
      const userId = _get(idObj, 'userId');
      const substanceUserId = _get(idObj, 'substanceUserId');

      const result = await fetchTransfers(resource, {
        userId,
        substanceUserId,
      });

      return result;
    }

    return { data: { id: 0 } };
  },

  getMany: async (resource, params) => Promise,

  getManyReference: (resource, params) => Promise,

  create: async (resource, params) => Promise,

  update: async (resource, params) => Promise,

  updateMany: async (resource, params) => Promise,

  delete: (resource, params) => Promise,

  deleteMany: (resource, params) => Promise,
};

async function fetchTransfers(resource, variables) {
  const response = await apolloClient.query({
    query: QUERY_TRANSFERS,
    variables,
  });

  const errorCode = _get(response, 'errors[0].extensions.code');
  if (errorCode === 'BAD_USER_INPUT:NO_SUBSTANCE_USER_ASSOCIATED') {
    throw new Error(`No Substance user associated`);
  }

  let result = parseResponse(response, resource);
  result.data.id = JSON.stringify(variables);
  result = addAssociated(result, variables.substanceUserId);
  result = formatLogs(result);

  return result;
}

function addAssociated(transferData, substanceUserId) {
  const result = _clone(transferData);

  result.data.associated = false;
  const resultSubstanceUserId = _get(result, 'data.transfer.substanceUserId');
  if (resultSubstanceUserId === substanceUserId) result.data.associated = true;
  if (substanceUserId === undefined && resultSubstanceUserId)
    result.data.associated = true;

  return result;
}

function formatLogs(transferData) {
  const result = _clone(transferData);
  const logs = _get(result, 'data.transfer.logs');
  result.data.transfer.assetsTransferLogs = [];
  result.data.transfer.pointsTransferLogs = [];

  _forEach(logs, (log) => {
    const keys = _keys(log);
    if (
      _some(keys, (key) =>
        _includes(['srcTransactionIds', 'destTransactionIds'], key)
      )
    ) {
      result.data.transfer.assetsTransferLogs.push(log);
    } else {
      result.data.transfer.pointsTransferLogs.push(log);
    }
  });

  return result;
}

export default transfersProvider;
